import { useEffect, useState } from 'react';

import { json, LoaderFunctionArgs } from '@remix-run/node';
import { useLoaderData, useSearchParams } from '@remix-run/react';
import Layout from '~/components/layout';
import StarRating from '~/components/star-rating';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '~/components/ui/breadcrumb';
import { calcAuctionHouseRatingFromAuctionReviews, cn } from '~/lib/utils';
import { getAuctionHouseDetailsBySlug } from '~/services/api/auction-houses';
import { getAuctionsList } from '~/services/api/auctions';
import { validateUser } from '~/services/api/auth';
import { getReviewsList } from '~/services/api/reviews';
import { AuctionStatus } from '~/types';
import { Home } from 'lucide-react';

import PastAuctions from './past-auctions';
import Reviews from './reviews';
import UpcomingAuctions from './upcoming-auctions';

const tabs = [
  { label: 'Upcoming Auctions', value: 'upcoming' },
  { label: 'Past Auctions', value: 'past' },
  { label: 'Reviews', value: 'reviews' },
];

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const slug = params.slug;

  if (!slug)
    throw new Response('Unable to retrieve details', {
      status: 500,
    });

  const url = new URL(request.url);
  const limit = Number(url.searchParams.get('limit')) || 12;
  const page = Number(url.searchParams.get('page')) || undefined;

  const { user } = await validateUser(request);

  const auctionHouse = await getAuctionHouseDetailsBySlug(slug);

  if (!auctionHouse) {
    throw new Response('Auction house not found', { status: 404 });
  }

  const pastAuctionsData = await getAuctionsList({
    auctionHouseId: auctionHouse.id,
    limit,
    page,
    expired: true,
    status: AuctionStatus.APPROVED,
  });
  const upcomingAuctionsData = await getAuctionsList({
    auctionHouseId: auctionHouse.id,
    limit,
    page,
    upcoming: true,
    status: AuctionStatus.APPROVED,
  });
  const reviewsData = await getReviewsList({
    auctionHouseId: auctionHouse.id,
    limit,
    page,
  });

  return json({
    user,
    auctionHouse,
    pastAuctionsData,
    upcomingAuctionsData,
    reviewsData,
  });
};

export default function AuctionHousesId() {
  const data = useLoaderData<typeof loader>();

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('upcoming');

  const { auctionHouse, pastAuctionsData, upcomingAuctionsData, user, reviewsData } = data;

  if (!auctionHouse) {
    throw new Response('Auction house not found', { status: 404 });
  }

  const auctionHouseReviews = auctionHouse.auctions.flatMap((a) => a.reviews);

  useEffect(() => {
    searchParams.delete('page');
    setSearchParams(searchParams, {
      preventScrollReset: true,
    });
  }, [activeTab]);

  return (
    <Layout user={user as unknown as NonNullable<Awaited<ReturnType<typeof validateUser>>['user']>}>
      <Breadcrumb className="mt-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/" className="flex flex-row items-center gap-1 text-sm font-normal">
              <Home className="h-5 w-5" />
              Home
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbLink>
            <BreadcrumbLink href="/auction-houses" className="text-sm font-normal">
              Auction Houses
            </BreadcrumbLink>
          </BreadcrumbLink>
          <BreadcrumbSeparator />
          <BreadcrumbItem>{auctionHouse.name}</BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="mt-10 flex flex-row items-center justify-between">
        <div className="flex flex-col gap-y-5">
          <div className="flex items-center gap-x-4">
            <img
              className="h-14 w-14 rounded-lg border object-contain"
              src={auctionHouse.image}
              alt="auction-house-logo"
            />
            <div className="flex flex-col gap-y-2">
              <h1 className="text-2xl font-medium">{auctionHouse.name}</h1>
              <div className="flex flex-row gap-x-3">
                <StarRating
                  rating={calcAuctionHouseRatingFromAuctionReviews(
                    auctionHouseReviews as unknown as NonNullable<
                      Awaited<ReturnType<typeof getAuctionHouseDetailsBySlug>>
                    >['auctions'][number]['reviews'],
                  )}
                  size={16}
                />
                <span className="text-xs text-slate-400">{auctionHouseReviews.length} reviews</span>
              </div>
            </div>
          </div>

          <div>
            <span className="text-sm">{auctionHouse.description}</span>
          </div>
        </div>

        <div className="border"></div>
      </div>

      <div className="flex items-start border-b md:px-2">
        {tabs.map((tab, index) => (
          <button
            key={index}
            className={cn(
              'px-3 py-2.5 text-left text-sm text-slate-600',
              activeTab === tab.value && 'rounded-none border-b-2 border-black font-semibold text-black',
            )}
            onClick={() => setActiveTab(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mb-20">
        {activeTab === 'upcoming' && (
          <UpcomingAuctions
            auctions={upcomingAuctionsData as unknown as NonNullable<Awaited<ReturnType<typeof getAuctionsList>>>}
          />
        )}
        {activeTab === 'past' && (
          <PastAuctions
            auctions={pastAuctionsData as unknown as NonNullable<Awaited<ReturnType<typeof getAuctionsList>>>}
          />
        )}
        {activeTab === 'reviews' && (
          <Reviews
            reviews={reviewsData as unknown as NonNullable<Awaited<ReturnType<typeof getReviewsList>>>}
            auctionHouseData={
              auctionHouse as unknown as NonNullable<Awaited<ReturnType<typeof getAuctionHouseDetailsBySlug>>>
            }
          />
        )}
      </div>
    </Layout>
  );
}
